import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { IoIosCloseCircle } from "react-icons/io";
import { useSwipeable } from "react-swipeable";
import {
  IoArrowBackCircleSharp,
  IoArrowForwardCircleSharp,
} from "react-icons/io5";
import { useMobileVH } from "../hooks/useMobileVH";

interface Document {
  name: string;
  url: string;
}
interface ViewerProps {
  images: Document[];
  isViewerImageOpen: boolean;
  onClose: () => void;
}

const ImageViewer: React.FC<ViewerProps> = ({
  images,
  isViewerImageOpen,
  onClose,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const closeDrawer = () => {
    setDrawerOpen(false);
    onClose();
  };

  const showPreviousImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images?.length - 1 : prevIndex - 1,
    );
  };

  const showNextImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images?.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const jumpTo = (index: number) => {
    setCurrentIndex(index);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      showNextImage();
    },
    onSwipedRight: () => {
      showPreviousImage();
    },
    trackMouse: true,
  });

  useMobileVH();

  useEffect(() => {
    if (isViewerImageOpen) {
      setDrawerOpen(true);
    } else {
      setDrawerOpen(false);
    }
  }, [isViewerImageOpen]);

  return (
    <div className="bg-custom-gray mt-4 mb-4 shadow-xl w-full overflow-hidden">
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={closeDrawer}
        onOpen={closeDrawer}
      >
        <div
          {...handlers}
          className="flex justify-center items-center bg-custom-shadow-blue bg-opacity-90 relative"
          style={{
            height: "calc(var(--vh, 1vh) * 100)",
          }}
        >
          <button
            onClick={closeDrawer}
            className="absolute top-4 right-4 text-custom-white bg-custom-salmon rounded-full p-2"
          >
            <IoIosCloseCircle className="text-4xl text-custom-white" />
          </button>

          <div className="w-full flex flex-col items-center overflow-hidden">
            <div>
              <button
                onClick={showPreviousImage}
                className="absolute top-1/2 left-2 sm:left-4 -translate-y-1/2 z-20
               flex items-center
               h-1/2  w-1/2 text-custom-salmon "
                aria-label="Previous Image"
                style={{
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <IoArrowBackCircleSharp
                  className={`md:text-6xl text-4xl transition-transform duration-200 hover:scale-110 ${
                    images.length > 1 ? "md:block hidden" : "hidden"
                  }`}
                />
              </button>

              {images?.length > 0 && (
                <img
                  src={images[currentIndex]?.url}
                  alt={images[currentIndex]?.name}
                  className="w-full object-contain"
                  style={{
                    maxHeight: "calc(var(--vh, 1vh) * 90)",
                    overflow: "hidden",
                  }}
                />
              )}

              <button
                onClick={showNextImage}
                className="absolute top-1/2 right-2 sm:right-4 -translate-y-1/2 z-20
               flex items-center justify-end
                w-1/2 h-1/2 text-custom-salmon"
                aria-label="Next Image"
                style={{
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <IoArrowForwardCircleSharp
                  className={`md:text-6xl text-4xl transition-transform duration-200 hover:scale-110 ${
                    images.length > 1 ? "md:block hidden" : "hidden"
                  }`}
                />
              </button>
            </div>

            <div className="flex space-x-2 mt-4 h-[2rem]">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => jumpTo(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentIndex === index
                      ? "bg-custom-white scale-125"
                      : "bg-custom-red"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};

export default ImageViewer;
